import React, { memo, useMemo } from 'react';
import { get, upperCase, capitalize } from 'lodash';
import Badges from 'modules/common/components/Badge';
import Button from 'partial/components/Button';
import PropTypes from 'prop-types';
import ThumbIcon from 'partial/components/ThumbIcon';
import Card from 'partial/components/Card';
import classNames from 'classnames';
import { aclHelper } from 'helper';

import { useOpenUpdateRequest } from '../modals/UpdateRequest';
import { useShowOrganization } from '../hooks';

const Details = ({
  name,
  mask_name_limit,
  rate_per_sms,
  max_purchase_limit,
  credit_limit,
}) => (
  <div className="p-6 space-y-3 text-sm font-semibold">
    <div>{capitalize(name)} Plan</div>
    <div>{rate_per_sms} per SMS</div>
    <div>
      {mask_name_limit === '0' ? 'Unlimited' : mask_name_limit} Mask Name
      {+mask_name_limit === 1 ? '' : 's'}
    </div>
    <div>
      {max_purchase_limit === '0' ? 'Unlimited' : max_purchase_limit} Purchase
      Limit
    </div>
    <div>{credit_limit === '0' ? 'Unlimited' : credit_limit} Credit Limit</div>
    <div>Free technical support</div>
  </div>
);

Details.propTypes = {
  name: PropTypes.string.isRequired,
  rate_per_sms: PropTypes.string.isRequired,
  mask_name_limit: PropTypes.string.isRequired,
  max_purchase_limit: PropTypes.string.isRequired,
  credit_limit: PropTypes.string.isRequired,
};

function RequestDetails({ data, callBack }) {
  const openUpdateRequestModal = useOpenUpdateRequest();
  const [isLoading, organization] = useShowOrganization(
    data?.organization_uuid,
  );
  const name = `${organization?.profile?.first_name} ${organization?.profile?.last_name}`;
  const mobile = organization?.profile?.mobile_number;
  const email = organization?.user?.email;
  const current = get(data, 'current_subscription') || {};
  const requested = get(data, 'requested_plan') || {};

  const renderActions = useMemo(() => {
    if (data?.status === 'PENDING')
      return (
        <div className="space-y-2">
          {aclHelper(
            'subscriber_subscription',
            'u',
            <>
              {' '}
              <Button
                size="xs"
                primary
                className="w-[150px] uppercase"
                onClick={() =>
                  openUpdateRequestModal({
                    ...data,
                    action: 'approve',
                    cb: callBack,
                  })
                }
              >
                Approve
              </Button>
              <Button
                size="xs"
                outline
                className="w-[150px] uppercase"
                onClick={() =>
                  openUpdateRequestModal({
                    ...data,
                    action: 'reject',
                    cb: callBack,
                  })
                }
              >
                Disapprove
              </Button>
            </>,
          )}
        </div>
      );
    return (
      <div>
        <Badges
          status={upperCase(data?.status)}
          containerClassName="!text-sm !px-8 w-fit"
        />
      </div>
    );
  }, [data, openUpdateRequestModal, callBack]);

  return (
    <div className="w-full px-4">
      <div className="flex flex-col gap-2 h-full">
        <div className="px-4 py-6 flex justify-between border-b border-neutral-300/50">
          {isLoading ? (
            <div className="w-3/5 grid grid-cols-2 gap-3">
              <div className="col-span-1 animate-pulse bg-neutral-300 h-5 w-full rounded" />
              <div className="col-span-1 animate-pulse bg-neutral-300 h-5 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-neutral-300 h-5 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-neutral-300 h-5 w-full rounded" />
            </div>
          ) : (
            <div className="flex gap-8">
              <ThumbIcon
                className="h-24 w-24 rounded-full"
                initialsClassName="text-5xl"
                alt={name}
                url={organization?.profile?.avatar || ''}
              />
              <div className="space-y-px">
                <div className="font-bold text-lg">{name}</div>
                <div className="text-sm">{data?.organization_name}</div>
                <div className="text-sm">{email}</div>
                <div className="text-sm">{mobile}</div>
                {data?.remarks && (
                  <div className="pt-2">
                    <div className={classNames('flex flex-col gap-px text-sm')}>
                      <div className="font-bold">Remarks:</div>
                      <div>{data?.remarks}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div>{renderActions}</div>
        </div>
        <Card className="mt-5 p-6 flex-1 space-y-4">
          <div className="flex gap-4 text-sm tracking-wider">
            <div className="font-semibold text-neutral-700">AGENCY:</div>
            <div className="font-bold">{data?.organization_name}</div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:gap-0 xl:grid-cols-2">
            <div className="border border-neutral-200/80 rounded-r xl:rounded-r-none rounded-l">
              <div className="py-2 tracking-wider text-center font-bold bg-neutral-200 text-neutral-900">
                CURRENT PLAN
              </div>
              <Details {...current} />
            </div>
            <div className="border xl:border-r border-y border-neutral-200/80 rounded-l xl:rounded-l-none xl:rounded-r">
              <div className="py-2 tracking-wider text-center font-bold bg-brand-600 text-white">
                REQUESTED PLAN
              </div>
              <Details {...requested} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

RequestDetails.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  callBack: PropTypes.func.isRequired,
};

export default memo(RequestDetails);
